/**
 * DoorLoop Prompt Templates
 * Templates for extracting fields from conversations for DoorLoop push integrations
 */
import { z } from 'zod';
/**
 * Schema for DoorLoop task field extraction response
 *
 * LLM extracts these fields from the conversation:
 * - priority: LOW, MEDIUM, or HIGH based on urgency (DoorLoop's native enum)
 *
 * Fields NOT extracted by LLM (set by push service):
 * - subject: From jobData.title
 * - status: Always 'NOT_STARTED' for new tasks
 * - description: From jobData.summary + conversation deeplink
 * - requestedBy* / assignedToUsers: From routing + handoff/metadata config
 */
export const DoorLoopTaskSchema = z.object({
    priority: z
        .enum(['LOW', 'MEDIUM', 'HIGH'])
        .describe('Task priority level (prefer MEDIUM unless urgency is clear)'),
    reasoning: z.string().describe('Brief explanation of the priority selection'),
});
/**
 * Prompt template for extracting DoorLoop task fields
 * Used to extract priority from the conversation
 */
export const doorloopTaskTemplate = {
    name: 'doorloop-task',
    version: '1.0.0',
    systemPrompt: `You are a property management assistant that extracts task information from customer conversations.

Your task is to carefully analyze the FULL conversation transcript between the caller and the AI assistant to determine the task's priority. Pay close attention to everything the caller says.

PRIORITY GUIDELINES:
- "HIGH": ONLY for true emergencies that pose immediate risk:
  * Active water leaks causing damage
  * No heat when outside temperature is below freezing
  * Gas smell or suspected gas leak
  * Electrical hazards (sparking, burning smell)
  * Security issues (broken locks, broken windows)
  * Sewage backup
  * No hot water in winter
- "MEDIUM": Most requests (DEFAULT - use this for typical issues)
  * Appliances not working properly
  * Minor plumbing issues (slow drains, dripping faucets)
  * HVAC issues that aren't emergencies
  * Billing or account questions that need follow-up
- "LOW": Non-urgent, quality-of-life items
  * Cosmetic requests
  * Minor inconveniences
  * General inquiries with no time pressure

When in doubt, use "MEDIUM" priority.`,
    userPrompt: `Analyze this conversation and extract the task priority:

ISSUE SUMMARY:
{{summary}}

FULL CONVERSATION TRANSCRIPT:
{{transcript}}

Instructions:
1. Read the ENTIRE transcript carefully - important details are often mentioned later in the conversation
2. Only set priority to "HIGH" for true emergencies`,
    variables: ['summary', 'transcript'],
    metadata: {
        maxTokens: 500,
        responseFormat: 'json_object',
    },
};
//# sourceMappingURL=doorloop.js.map